import { memo, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import FiltersBar from '@/components/FiltersBar';
import {
  drugScreenSortOptions,
  drugScreenTestTypeOrder,
  resultOptions
} from '@/constants/drugScreen';
import useFilterParams, { type FilterFields, type FilterValues } from '@/hooks/useFilterParams';
import Accordion from '@/ui/components/Accordion';
import ActiveFilters from '@/ui/components/ActiveFilters';
import CheckboxFilterGroup from '@/ui/components/CheckboxFilterGroup';
import FilterApplyButton from '@/ui/components/FilterApplyButton';
import type { OptionType, defaultOptionTypes } from '@/ui/components/Form/Select/types';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

import type { Props } from './types';

const KIND_LABELS = Object.fromEntries(
  Object.keys(drugScreenTestTypeOrder).map((kind) => [kind, snakeCaseToTitleCase(kind)])
);

const FILTER_FIELDS: FilterFields = {
  kind: { kind: 'multi', label: 'Kind', options: KIND_LABELS },
  result: { kind: 'multi', label: 'Result', options: resultOptions }
};

const toOptions = (name: string, labels: Record<string, string>) =>
  Object.entries(labels).map(([value, label]) => ({ label, name, value }));

const KIND_OPTIONS = toOptions('kind', KIND_LABELS);
const RESULT_OPTIONS = toOptions('result', resultOptions);

const DrugScreensFilterForm = ({ activeParams, setParams }: Props) => {
  const { activeGroups, apply, clear, removeValue, values } = useFilterParams({
    fields: FILTER_FIELDS,
    searchParams: activeParams,
    setSearchParams: setParams
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
    register
  } = useForm<FilterValues>({ values });

  const updateSort = useCallback(
    (option: OptionType<defaultOptionTypes>) => {
      if (option.value)
        setParams((currentParams) => {
          currentParams.set('ordering', option.value as string);
          currentParams.set('page', '1');
          return currentParams;
        });
    },
    [setParams]
  );

  return (
    <form className="relative" onSubmit={handleSubmit(apply)}>
      <FiltersBar
        filterSets={
          <div className="flex flex-col gap-1">
            <Accordion buttonText="Result">
              <CheckboxFilterGroup
                idPrefix="drug-screen-result-filter"
                options={RESULT_OPTIONS}
                register={register}
              />
              <FilterApplyButton isSubmitting={isSubmitting} />
            </Accordion>
            <Accordion buttonText="Kind">
              <CheckboxFilterGroup
                idPrefix="drug-screen-kind-filter"
                options={KIND_OPTIONS}
                register={register}
              />
              <FilterApplyButton isSubmitting={isSubmitting} />
            </Accordion>
          </div>
        }
        sort={{
          onChange: updateSort,
          sortOptions: drugScreenSortOptions,
          value: activeParams.get('ordering')
        }}>
        <ActiveFilters groups={activeGroups} onClearAll={clear} onRemove={removeValue} />
      </FiltersBar>
    </form>
  );
};

export default memo(DrugScreensFilterForm);
